import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import axios from '@/utils/api';
import { API } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Check, X } from '@phosphor-icons/react';

export default function AdminUsers() {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const [pendingRes, allRes] = await Promise.all([
        axios.get(`${API}/admin/users/pending`),
        axios.get(`${API}/admin/users`)
      ]);
      setPendingUsers(pendingRes.data);
      setAllUsers(allRes.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (userId, status) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, null, {
        params: { status }
      });
      toast.success(`User ${status}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      publisher: 'bg-[#f91445]/15 text-[#f91445] border-[#f91445]/40',
      brand: 'bg-[#7c3aed]/15 text-[#7c3aed] border-[#7c3aed]/40',
      admin: 'bg-[var(--adm-chip)] text-[var(--adm-text)] border-[var(--adm-border-strong)]'
    };
    return colors[role] || 'bg-[var(--adm-chip)] text-[var(--adm-text)] border-[var(--adm-border-strong)]';
  };

  const getStatusBadge = (status) => {
    const colors = {
      approved: 'bg-accent border-foreground',
      pending: 'bg-background border-foreground',
      rejected: 'bg-destructive/20 border-destructive',
      suspended: 'bg-muted border-foreground'
    };
    return colors[status] || 'border-foreground';
  };

  if (loading) {
    return (
      <DashboardLayout role="admin">
        <div className="p-8">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="admin">
      <div className="p-8" data-testid="admin-users-page">
        <h1 className="font-heading text-4xl font-bold mb-8">User Management</h1>

        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending ({pendingUsers.length})
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">
              All Users ({allUsers.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            {pendingUsers.length === 0 ? (
              <div className="rounded-xl border border-[var(--adm-border)] bg-[var(--adm-surface)] p-12 text-center">
                <p className="text-[var(--adm-muted)]">No pending verifications</p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] border border-[#E5E7EB]">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E5E7EB] bg-[#F8F9FA]">
                        <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Email</th>
                        <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Role</th>
                        <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Company</th>
                        <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Website</th>
                        <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Registered</th>
                        <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingUsers.map((user) => (
                        <tr
                          key={user.id}
                          className="h-12 border-b border-[#F3F4F6] transition-colors hover:bg-[#F8F9FB]"
                          data-testid={`pending-user-${user.id}`}
                        >
                          <td className="px-4 py-2 text-[#111827]">{user.email}</td>
                          <td className="px-4 py-2">
                            <span className="inline-flex items-center gap-2 text-sm text-[#374151]">
                              <span className={`inline-block h-2 w-2 rounded-full ${user.role === 'brand' ? 'bg-blue-500' : user.role === 'publisher' ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                              {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-[#6B7280]">{user.company_name || '-'}</td>
                          <td className="px-4 py-2">
                            {user.website ? (
                              <a href={user.website} target="_blank" rel="noopener noreferrer" className="text-[#DC120F] hover:underline text-sm">
                                Link
                              </a>
                            ) : <span className="text-[#9CA3AF]">-</span>}
                          </td>
                          <td className="px-4 py-2 text-sm text-[#6B7280]">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleStatusUpdate(user.id, 'approved')}
                                className="bg-accent text-foreground hover:bg-accent/80"
                                data-testid={`approve-${user.id}`}
                              >
                                <Check size={16} weight="bold" className="mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleStatusUpdate(user.id, 'rejected')}
                                className="border-destructive text-destructive hover:bg-destructive hover:text-white"
                                data-testid={`reject-${user.id}`}
                              >
                                <X size={16} weight="bold" className="mr-1" />
                                Reject
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="all">
            <div className="overflow-hidden rounded-xl bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] border border-[#E5E7EB]">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E5E7EB] bg-[#F8F9FA]">
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Email</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Role</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Status</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Company</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Registered</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers.map((user) => (
                      <tr
                        key={user.id}
                        className="h-12 border-b border-[#F3F4F6] transition-colors hover:bg-[#F8F9FB]"
                        data-testid={`user-${user.id}`}
                      >
                        <td className="px-4 py-2 text-[#111827]">{user.email}</td>
                        <td className="px-4 py-2">
                          <span className="inline-flex items-center gap-2 text-sm text-[#374151]">
                            <span className={`inline-block h-2 w-2 rounded-full ${user.role === 'brand' ? 'bg-blue-500' : user.role === 'publisher' ? 'bg-emerald-500' : 'bg-violet-500'}`} />
                            {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span className="inline-flex items-center gap-2 text-sm text-[#374151]">
                            <span className={`inline-block h-2 w-2 rounded-full ${user.status === 'approved' ? 'bg-emerald-500' : user.status === 'suspended' ? 'bg-red-500' : user.status === 'rejected' ? 'bg-red-400' : 'bg-amber-400'}`} />
                            {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-[#6B7280]">{user.company_name || '-'}</td>
                        <td className="px-4 py-2 text-sm text-[#6B7280]">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2">
                          {user.status === 'approved' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusUpdate(user.id, 'suspended')}
                              className="border-foreground hover:bg-muted"
                            >
                              Suspend
                            </Button>
                          )}
                          {user.status === 'suspended' && (
                            <Button
                              size="sm"
                              onClick={() => handleStatusUpdate(user.id, 'approved')}
                              className="bg-accent text-foreground"
                            >
                              Reactivate
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
